import {Injectable} from '@angular/core';
import {CoreService} from '../../core/service';
import {INotification} from '../models';
import {NotificationTypeEnum} from '../models/notification-types.enum';
import {map, Observable} from 'rxjs';

import {
  collection,
  collectionData,
  doc,
  Firestore,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import type {
  CollectionReference,
  DocumentData,
  DocumentReference,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  QuerySnapshot,
} from 'firebase/firestore';
import {path_notification_id, path_notifications} from '../../../shared/shared-services/database-paths';

type WithId<T, IdKey extends string> = T & Record<IdKey, string>;

/**
 * Optional filters when resolving a notification from an API event id
 * (stock take id, order id, stock-update event id, etc.).
 */
export interface INotificationEventLookupOptions {
  storeId?: string;
  type?: NotificationTypeEnum | string;
}

@Injectable({providedIn: 'root'})
export class NotificationService extends CoreService {
  #notificationPageSize = 20;

  constructor(protected override firestore: Firestore) {
    super(firestore);
  }

  // converter to enforce <T>
  private static converter<T>(): FirestoreDataConverter<T> {
    return {
      toFirestore: (data: T): DocumentData => data as unknown as DocumentData,
      fromFirestore: (snap: QueryDocumentSnapshot): T => snap.data() as T,
    };
  }

  //#region GET DOCUMENT AS PROMISE
  async getDocumentAsPromise<DataType>(
    path: string,
  ): Promise<DataType | undefined> {
    const ref: DocumentReference<DataType> = doc(
      this.firestore,
      path,
    ).withConverter(NotificationService.converter<DataType>());
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : undefined;
  }

  //#endregion

  //#region UNREAD NOTIFICATIONS STREAM
  getUnreadNotifications$(
    path: string,
    storeIds: string[],
  ): Observable<WithId<INotification, 'notificationId'>[]> {
    const col: CollectionReference<INotification> = collection(
      this.firestore,
      path,
    ).withConverter(NotificationService.converter<INotification>());

    const q = query(
      col,
      where('storeId', 'in', storeIds),
      where('isNotificationRead', '==', false),
      orderBy('timestamp', 'desc'),
    );

    return collectionData(q, {idField: 'notificationId'});
  }

  //#endregion

  //#region UNSUCCESSFUL UNREAD STREAM
  getUnsuccessfulUnreadNotifications$(
    path: string,
    userId: string,
    storeIds: string[],
  ): Observable<WithId<INotification, 'notificationId'>[]> {
    const col: CollectionReference<INotification> = collection(
      this.firestore,
      path,
    ).withConverter(NotificationService.converter<INotification>());

    const q = query(
      col,
      where('userId', '==', userId),
      where('storeId', 'in', storeIds),
      where('isNotificationRead', '==', false),
      where('success', '==', 'false'),
      orderBy('timestamp', 'desc'),
    );

    return collectionData(q, {idField: 'notificationId'}).pipe(
      map((rows) =>
        rows.map((n) => ({
          ...n,
          isNotificationRead:
            !!(Object.prototype.hasOwnProperty.call(n, 'isNotificationRead') &&
              n.isNotificationRead),
        })),
      ),
    );
  }

  //#endregion

  //#region LIVE NOTIFICATIONS
  getLiveNotifications(
    path: string,
    storeIds: string[],
  ): Observable<WithId<INotification, 'notificationId'>[]> {
    const col: CollectionReference<INotification> = collection(
      this.firestore,
      path,
    ).withConverter(NotificationService.converter<INotification>());

    const q = query(
      col,
      where('storeId', 'in', storeIds),
      orderBy('timestamp', 'desc'),
      limit(this.#notificationPageSize),
    );

    return collectionData(q, {idField: 'notificationId'}).pipe(
      map((rows) =>
        rows.map((n) => ({
          ...n,
          isNotificationRead:
            !!(Object.prototype.hasOwnProperty.call(n, 'isNotificationRead') &&
              n.isNotificationRead),
        })),
      ),
    );
  }

  //#endregion

  //#region PAGED NOTIFICATIONS
  async getLatestNotificationsPaged(
    path: string,
    storeIds: string[],
    lastDocId?: string,
  ): Promise<{
    snapshot: QuerySnapshot<INotification>;
    hasMore: boolean;
  }> {
    if (storeIds.length > 10) {
      throw new Error("Firestore 'in' queries support a maximum of 10 values");
    }

    const col: CollectionReference<INotification> = collection(
      this.firestore,
      path,
    ).withConverter(NotificationService.converter<INotification>());

    let q = query(col, where('storeId', 'in', storeIds), orderBy('timestamp', 'desc'));

    if (lastDocId) {
      const lastDocRef: DocumentReference<INotification> = doc(
        this.firestore,
        `${path}/${lastDocId}`,
      ).withConverter(NotificationService.converter<INotification>());
      const lastSnap = await getDoc(lastDocRef);
      if (lastSnap.exists()) {
        q = query(q, startAfter(lastSnap));
      }
    }

    const snapshot = await getDocs(query(q, limit(this.#notificationPageSize)));
    const hasMore = snapshot.size === this.#notificationPageSize;

    return {snapshot, hasMore};
  }

  //#endregion

  //#region NOTIFICATION LOOKUP BY EVENT ID
  /**
   * Resolves notification documents whose `event` field matches an API event id
   * (stock take id, order id, stock-update event id, etc.).
   *
   * Prefers `storeId + event` when a store is provided (typical security-rule shape),
   * then falls back to an event-only query. Type filtering is applied in memory.
   */
  async findNotificationsByEventId(
    eventId: string,
    options?: INotificationEventLookupOptions,
  ): Promise<WithId<INotification, 'notificationId'>[]> {
    const trimmedEventId = eventId?.trim();
    if (!trimmedEventId) {
      return [];
    }

    const col: CollectionReference<INotification> = collection(
      this.firestore,
      path_notifications(),
    ).withConverter(NotificationService.converter<INotification>());

    let notifications: WithId<INotification, 'notificationId'>[] = [];

    if (options?.storeId) {
      try {
        const storeEventQuery = query(
          col,
          where('storeId', '==', options.storeId),
          where('event', '==', trimmedEventId),
        );
        const storeEventSnapshot = await getDocs(storeEventQuery);
        notifications = storeEventSnapshot.docs.map((docSnap) => ({
          ...docSnap.data(),
          notificationId: docSnap.id,
        }));
      } catch (error) {
        console.warn(
          '[Notifications] storeId+event lookup failed, falling back to event-only:',
          trimmedEventId,
          error,
        );
      }
    }

    if (notifications.length === 0) {
      const eventQuery = query(col, where('event', '==', trimmedEventId));
      const eventSnapshot = await getDocs(eventQuery);
      notifications = eventSnapshot.docs.map((docSnap) => ({
        ...docSnap.data(),
        notificationId: docSnap.id,
      }));
      if (options?.storeId) {
        notifications = notifications.filter(
          (notification) => notification.storeId === options.storeId,
        );
      }
    }

    if (!options?.type) {
      return notifications;
    }

    return notifications.filter(
      (notification) => String(notification.type) === String(options.type),
    );
  }

  /**
   * Convenience: first notification id for an event, or `null` if none found.
   */
  async getNotificationIdByEventId(
    eventId: string,
    options?: INotificationEventLookupOptions,
  ): Promise<string | null> {
    const notifications = await this.findNotificationsByEventId(eventId, options);
    return notifications[0]?.notificationId ?? null;
  }

  /**
   * Sets `retried: true` on matching failed notifications for an event id.
   * Writes only the `retried` field so missing optional fields cannot block the update.
   *
   * @returns Updated notification documents (with `retried: true`).
   */
  async markNotificationsRetriedByEventId(
    eventId: string,
    options?: INotificationEventLookupOptions,
  ): Promise<WithId<INotification, 'notificationId'>[]> {
    const notifications = await this.findNotificationsByEventId(eventId, options);
    const updated: WithId<INotification, 'notificationId'>[] = [];

    for (const notification of notifications) {
      if (this.isNotificationSuccessful(notification) || notification.retried) {
        continue;
      }

      const path = path_notification_id(notification);
      const docRef = doc(this.firestore, path);
      await updateDoc(docRef, {retried: true});
      updated.push({
        ...notification,
        retried: true,
      });
    }

    return updated;
  }

  /**
   * Treats boolean `false` and string `'false'` as unsuccessful.
   */
  private isNotificationSuccessful(notification: INotification): boolean {
    const success = notification.success as boolean | string | undefined;
    return success === true || success === 'true';
  }

  //#endregion
}
